import React, { useState, useRef } from "react";
import "./contentpage.css";
import Question from "../components/Question";
import LandingSection from "./LandingSection";
import FloatingPageNav from "../components/FloatingPageNav";
import BadgePopup from "../components/Badgepopup";

function IslamicGuide61() {
  const contentRef = useRef(null);
  const [isOpen, setIsOpen] = useState(true);
  const titleText = "Bitcoin as al-mal (wealth/money/property)";
  const links = {
    prev: "/islamicguide-61",
    index: "/index-islamicguide",
    next: "/islamicguide-62",
  };

  const questions = [
    {
      text: "1. Based on the definitions of al-mal by Ibn Abidin and other scholars, what is a key attribute of wealth?",
      options: [
        {
          value: "A",
          label: "A. It must be a physical object like gold or silver.",
        },
        { value: "B", label: "B. It must have a fixed, unchanging value." },
        {
          value: "C",
          label: "C. It must be subject to government regulation and taxation.",
        },
        {
          value: "D",
          label:
            "D. It must be something that people desire and can be saved for later use.",
        },
      ],
      correct: "D",
    },
    {
      text: "2. Which scholar’s definition of al-mal introduces the concept of permissibility?",
      options: [
        { value: "A", label: "A. Ibn Taymiyah" },
        { value: "B", label: "B. Sharaf al-Din al-Maqdisi" },
        { value: "C", label: "C. Al-Suyuti" },
        { value: "D", label: "D. Ibn al-Qayyim" },
      ],
      correct: "B",
    },
    {
      text: "3. How does the attribute of value (qimah) apply to Bitcoin as a form of al-mal?",
      options: [
        {
          value: "A",
          label: "A. Bitcoin's value is determined by a central bank.",
        },
        {
          value: "B",
          label: "B. Bitcoin has value only because it is a physical asset.",
        },
        {
          value: "C",
          label:
            "C. Bitcoin's value is derived from its scarcity and global market acceptance.",
        },
        {
          value: "D",
          label: "D. Bitcoin has no intrinsic value, as it is purely digital.",
        },
      ],
      correct: "C",
    },
    {
      text: "4. In the context of al-mal, why is the concept of guarantee (dhaman) important?",
      options: [
        {
          value: "A",
          label:
            "A. It implies that the asset is safe from all forms of theft.",
        },
        {
          value: "B",
          label:
            "B. It holds a person liable for compensating the owner if they wrongfully destroy or take their property.",
        },
        {
          value: "C",
          label:
            "C. It means the government promises to protect the asset's value from market fluctuations.",
        },
        {
          value: "D",
          label:
            "D. It ensures the asset can only be used for permissible activities.",
        },
      ],
      correct: "B",
    },
  ];

  // State to track selected answers for all questions
  const [answers, setAnswers] = useState({});

  // Function to update an answer
  const handleAnswerChange = (questionIndex, selectedValue) => {
    setAnswers((prev) => ({ ...prev, [questionIndex]: selectedValue }));
  };

  // Compute score
  const score = questions.reduce((total, q, index) => {
    return total + (answers[index] === q.correct ? 1 : 0);
  }, 0);

  return (
    <div className="container">
      <LandingSection
        titleText={titleText}
        scrollToRef={contentRef}
        progressValue={61}
        timerMinutes={60}
        currentLevel={5}
        unlockedBadges={9}
      />

      <p className="content" ref={contentRef}>
        Welcome to <b>Day 61</b>! In this lesson, we examine Bitcoin through the
        classical Islamic framework of al-mal (wealth), grounding the discussion
        in the foundational principle of default permissibility in Islamic law.
        By applying time-tested juristic definitions to a modern digital
        phenomenon, this lesson aims to assess whether Bitcoin fulfills the
        essential attributes of lawful wealth, moving beyond speculation and
        novelty toward a principled, text-based analysis rooted in the Qur’an,
        Sunnah, and scholarly tradition.
        <br />
        The nature of wealth, a concept as old as human civilization itself, has
        undergone a profound and rapid transformation in the digital age. From
        the shells and spices of ancient times to the gold, silver, and paper
        money that have dominated for centuries, the medium of exchange and
        store of value has consistently evolved. Today, humanity stands on the
        precipice of another fundamental shift with the advent of digital money,
        and none is more emblematic of this change than Bitcoin. As this new
        form of digital cash garners global attention, it prompts a crucial and
        pressing question for Islamic jurisprudence: does Bitcoin fit the
        classical definition of al-mal (wealth) as articulated by the scholars
        of Islam? This inquiry is not merely a modern-day academic exercise but
        a vital exploration of how timeless principles can be applied to a new
        and unprecedented reality. A deep and thorough examination of the
        definitions of al-mal given by luminaries of Islam is required to
        understand whether Bitcoin can fulfil the role of money in an islamic
        society.
        <br />
        Scholars of Islam who refer to the Hanafi school of thought have
        describe wealth (al-mal) as: "That which one's nature inclines towards
        and which can be saved for a time of need." Al-Suyuti defined it,
        stating: "As for 'al-mal', Al-Shafi'i, may Allah have mercy on him,
        said: The name 'al-mal' is only applied to something that has a value
        for which it can be sold(traded), however small, and which people do not
        discard, such as a coin or the like." Some other scholars from the
        Shafi'i school of thought have said: "Wealth is anything that is
        beneficial, meaning prepared to be benefited from, and it is either a
        tangible assets or a beneficial service." Sharaf al-Din al-Maqdisi
        al-Hanbali said: "Wealth is that which is permissible to benefit from
        absolutely, or to acquire without a need or necessity."
        <br />
        <br />
        <b>Is Bitcoin al-mal?</b> <br />
        <br />
        Based on the provided definitions, the attributes of al-mal are:
        <br />
        <ul>
          <li>
            Desirability: It is something that a person's nature is inclined to
            acquire or possess
          </li>
          <li>Storability: It can be saved or preserved for a time of need</li>
          <li>Value: It has a recognized worth for which it can be sold</li>
          <li>
            Non-discardable: People do not simply throw it away because it is
            worthless
          </li>
          <li>
            Beneficial: It has utility or can be used for a lawful purpose
          </li>
          <li>
            Permissibility: Its acquisition and use are not forbidden by Islamic
            law
          </li>
          <li>
            Guaranteable: Its value is guaranteed, meaning compensation is due
            if it is wrongfully destroyed or taken.
          </li>
        </ul>
        The starting point for this analysis must be the fundamental principle
        governing all matters of life in Islam, particularly those which are new
        and unprecedented. This principle, articulated by the great jurist and
        scholar Ibn Taymiyyah, is that everything is permissible (al-asl fi
        al-ashya' al-ibahah) unless there is a clear and explicit evidence from
        the Qur'an or the Sunnah that declares it to be forbidden. This is a
        cornerstone of Islamic law, ensuring that the believers are not unduly
        restricted and that the vast bounty of Allah's creation remains
        accessible and beneficial to them. The burden of proof, therefore, lies
        not with the one who seeks to utilize a new commodity or technology, but
        with the one who seeks to prohibit it. Any prohibition must be based on
        a concrete textual source from the divine revelation, not on
        speculation, fear, or mere analogy to something impermissible. This
        principle is deeply rooted in the Quranic teachings itself
        <br />
        "He is the One Who created everything in the earth for you. Then He
        turned towards the heaven, forming it into seven heavens. And He has
        ˹perfect˺ knowledge of all things."{" "}
        <a href="https://quran.com/2/29" text="">
          (Surah Al-Baqarah 2:27)
        </a>
        <br />
        This verse, while seemingly about food, establishes a broader,
        overarching principle for all of Allah's provision. It is a direct
        command to consume what is lawful (halal) and good (tayyib), and a stern
        warning not to follow the path of Shaytan, who seeks to mislead mankind
        by making lawful things unlawful. This divine directive underscores the
        default state of permissibility and places the responsibility on
        individuals to engage with what is lawful while being cautious of the
        clear prohibitions. A commodity or medium of exchange, therefore, enters
        the realm of permissibility by default, and its use is considered lawful
        until a clear evidence from the foundational texts declares it
        otherwise.
        <br />
        <br />
        <br />
        <b>
          Bitcoin and the Classical Criteria of Desirability, Storability, and
          Value
        </b>
        <br />
        <br /> This principle of permissibility is also intrinsically linked to
        the inherent right of every individual to own and possess wealth. Islam
        recognizes the sanctity of private property and the right of an
        individual to acquire, use, and dispose of their wealth through lawful
        means. This right is divinely sanctioned, and to infringe upon it
        without just cause is a grave injustice. To claim that a form of wealth
        is forbidden requires a compelling and unequivocal argument based on the
        divine sources of law. The individual's right to earn and possess is a
        cornerstone of the Islamic economic system, and any assertion that a
        form of wealth is invalid or unlawful must be substantiated by a clear
        legal argument, not by a subjective interpretation or a blanket
        generalization.
        <br />
        With this foundational understanding established, we can now proceed to
        the core of the matter: an in-depth examination of Bitcoin through the
        lens of the classical definitions of al-mal. The scholars' definitions,
        as noted, provide a comprehensive framework for this analysis. They
        revolve around a set of key attributes that determine if something can
        be legitimately classified as wealth.
        <br />
        One of the most profound aspects of Bitcoin, and one that directly
        aligns with the definition provided by the jurists of Islam, is its
        undeniable desirability (mayl al-tab') and its capacity for storability
        and preservation (idkhar). The definition of al-mal as "that which one's
        nature inclines towards, and which can be saved for a time of need" is
        remarkably prescient when applied to the digital realm. The inclination
        towards Bitcoin is a phenomenon that is both a product of its design and
        a reflection of a deep-seated human desire for a stable and sovereign
        form of value. Unlike fiat currencies, which can be inflated into
        worthlessness at the whim of central banks, Bitcoin’s supply is
        mathematically capped at 21 million coins, a fact known to everyone on
        the network. This inherent and verifiable scarcity creates a profound
        desirability that is not manufactured but rather a logical response to a
        world where monetary policies often erode the value of savings. The
        desire for a deflationary asset, a form of wealth that cannot be
        debased, is a natural human inclination, and Bitcoin satisfies this
        desire in a way that no other digital asset has. This scarcity, combined
        with the immutability and censorship-resistance of it's blockchain,
        makes Bitcoin a highly sought-after commodity for a global population
        weary of centralized control and monetary manipulation.
        <br />
        Furthermore, Bitcoin's capacity for storability and preservation is a
        revolutionary feature that directly fulfills second criterion. Bitcoin
        can be stored for a time of need with an ease and security that is
        unparalleled by physical assets. A person can hold their entire life
        savings in a tiny hardware wallet, on a secure USB drive, or even simply
        as a twelve or twenty-four-word phrase memorized in their mind. This
        ability to carry one’s wealth securely across borders, without the need
        for a bank or a physical vault, is a testament to its exceptional
        storability. It is a form of wealth that is both highly portable and
        extraordinarily difficult to seize or confiscate, thereby preserving its
        value for future use. The preservation of wealth, in the Islamic sense,
        is not just about its monetary value but also about its security and
        safety from harm or misappropriation. Bitcoin's decentralized nature and
        cryptographic security offer a form of preservation that is
        fundamentally different from and in many ways superior to traditional
        forms of wealth.
        <br />
        This leads us to the second pillar of the definition of al-mal: its
        economic value and tradability. Al-Suyuti, quoting Al-Shafi'i, stated
        that wealth is "something that has a value for which it can be sold,
        however small, and which people do not discard." This definition
        emphasizes the marketplace as the ultimate arbiter of value. Bitcoin's
        value is derived from the very fact that it is a tradable commodity. It
        is an open, global, and permissionless network that allows anyone to
        participate in its economy. Its utility as a store of value is directly
        tied to its tradability, as it can be bought and sold for various goods,
        services, and other currencies across the world. The fact that it is not
        "discarded by people" is evident in the global community of users who
        not only hold it for a long term (a practice known as "HODLing") but
        also use it for daily transactions. The constant activity on the
        blockchain, the buying, spending, and transferring of Bitcoin, is a
        clear indication that it is not something people throw away but rather
        something they actively seek to acquire and utilize. Its value is not
        based on a single authority's decree but on the collective consensus of
        a vast and growing network of users, making its economic value a
        reflection of genuine demand and utility.
        <br />
        Bitcoin's value is also a product of the immense computational power
        required to maintain its network's security. This "proof-of-work"
        mechanism, while often misunderstood, is what ensures the integrity and
        finality of transactions. The cost of electricity and specialized
        hardware incurred by miners to secure the network creates a form of
        inherent value. The fact that the network is secured by a real-world,
        tangible cost, ties Bitcoin's economic value to a form of physical
        reality, even though the asset itself is digital. This stands in stark
        contrast to fiat currencies, which are not tied to any physical asset or
        cost of production and can be created out of thin air. Thus, Bitcoin's
        economic value is both a product of its scarcity and its security,
        fulfilling the criteria of a commodity that is not discarded but is
        valued and traded by people.
        <br />
        <br />
        <b>Beneficial Utility and the Rational Conclusion</b>
        <br />
        <br />
        The third crucial aspect of the definition of al-mal is its beneficial
        utility (manfa'a). He stated that wealth is "anything that is
        beneficial, meaning prepared to be benefited from." The benefits of
        Bitcoin are numerous and transformative, extending far beyond simple
        monetary transactions. At its core, Bitcoin provides a
        censorship-resistant and decentralized form of money. This utility is
        profoundly beneficial for individuals living in politically unstable
        regimes, where their savings are at constant risk of confiscation. It is
        also beneficial for those who are unbanked, providing them with a way to
        store and transact with value without the need for a traditional
        financial institution. Bitcoin's open and permissionless nature means
        that anyone with an internet connection can access it, making it a
        powerful tool for financial inclusion and empowerment.
        <br />
        Moreover, Bitcoin's utility extends to its role as a peer-to-peer
        payment system. It allows for the direct transfer of value between two
        parties without the need for a trusted third-party intermediary. This
        eliminates the fees, delays, and complexities associated with
        traditional banking systems, particularly for international
        transactions. For a merchant, it offers a secure and fast way to accept
        payments. For a freelancer, it provides a simple way to receive payment
        from clients anywhere in the world. This direct and efficient utility is
        a clear and tangible benefit, demonstrating that Bitcoin is indeed
        "prepared to be benefited from." This beneficial use case, as a medium
        of exchange and a tool for financial freedom, solidifies its position as
        a form of wealth that is not just valuable but also profoundly useful.
        <br />
        In conclusion, the scholarly definitions of al-mal provide a robust
        framework for understanding and classifying new forms of wealth.
        Bitcoin, a product of the digital age, fits this framework seamlessly.
        It possesses an inherent desirability rooted in its verifiable scarcity
        and sovereign nature. It offers an unparalleled ability for storability
        and preservation, fulfilling the need to save wealth for future use. Its
        economic value is established through its global market and the tangible
        cost of securing its network, ensuring it is a commodity that is valued
        and not discarded. Finally, its beneficial utility is undeniable,
        serving as a powerful tool for financial inclusion, a store of value,
        and a medium of exchange.
        <br />
        The assertion that a commodity is forbidden requires an explicit and
        undeniable legal argument, a burden of proof that proponents of a
        prohibition on Bitcoin have been unable to meet. The principle of
        default permissibility, coupled with the individual's divinely
        sanctioned right to own and acquire wealth, places Bitcoin squarely in
        the category of lawful assets. As the world continues to evolve, Islamic
        jurisprudence, with its timeless principles and adaptive spirit,
        provides a clear and just path for its followers to navigate this new
        frontier of wealth, allowing them to benefit from innovations that are
        both lawful and good. Bitcoin, in its essence, represents not just a
        technological advancement but a modern embodiment of the very attributes
        that have defined wealth in Islam for centuries. Its legitimacy as
        al-mal is not a matter of speculation but a logical conclusion drawn
        from the foundational sources of Islamic law. It is a form of wealth
        that is both desired by nature and useful for mankind, a testament to
        the wisdom and foresight of a legal system that remains relevant and
        vibrant for all ages.
        <br />
        <br />
        <b>Summary of Today’s Lesson</b>
        <br />
        <br />
        Today we learned how the term al-mal, which translates to "wealth,"
        "property," or "money," holds a specific and profound meaning in Islamic
        jurisprudence. It is defined by scholars as anything that has value,
        provides a benefit, can be stored for future needs, and whose use is
        permissible under Islamic law. Our analysis shows that Bitcoin possesses
        all these attributes. Its scarcity and global market acceptance give it
        value, while its function as a secure store of value and efficient
        medium of exchange makes it beneficial and storable. Furthermore, the
        permissibility of Bitcoin is established because it is not an inherently
        forbidden object and its use for lawful purposes is widely recognized.
        Therefore, Bitcoin can be considered a legitimate form of al-mal in
        Islam, subject to the same financial rulings as traditional forms of
        wealth.
      </p>
      <h2>Test your understanding</h2>
      {questions.map((q, index) => (
        <Question
          key={index}
          questionText={q.text}
          options={q.options}
          correctAnswer={q.correct}
          onAnswer={(value) => handleAnswerChange(index, value)}
        />
      ))}

      <div style={{ marginTop: "20px", fontWeight: "bold", fontSize: "1.2em" }}>
        Score: {score} / {questions.length}
      </div>

      <FloatingPageNav
        prevLink={links.prev}
        indexLink={links.index}
        nextLink={links.next}
      />
      <BadgePopup
        isOpen={isOpen}
        setIsOpen={setIsOpen}
        level="Level 5"
        badgeImg="./achieve.png"
        heading="Riba Renouncer"
        subtitle="Beginner - Bitcoin Guide for Muslims"
        mainImg="./ribarenouncer.png"
        description="Awarded for initiating your Riba Inquiry. You've recognized the imperative to seek money that is structurally just and have begun the legal and technical investigation into Bitcoin's Sharīʿah compliance."
      />
    </div>
  );
}

export default IslamicGuide61;
